import { Component, ElementRef, HostBinding, HostListener, effect, inject } from '@angular/core';
import { CtrlKeyService } from '../services/ctrl-key.service';
import { NavigationService } from '../services/navigation.service';
import { RuleId } from '../models/rule-id';
import { SettingsService } from '../services/settings.service';

/**
 * Base class for components that support filter rule navigation.
 * Provides shared functionality for:
 * - Ctrl/Cmd+click navigation to filter output
 * - Hover effects when Ctrl/Cmd is held
 * - Rule ID binding (single or multiple)
 */
@Component({
  template: ''
})
export abstract class FilterNavigationBase {
  protected ctrlKeyService!: CtrlKeyService;
  protected navigationService!: NavigationService;
  protected settings!: SettingsService;
  protected elementRef!: ElementRef<HTMLElement>;
  protected isHovered = false;

  /**
   * Single rule ID associated with this filter component.
   * Can be set via @Input() in extending components or programmatically.
   */
  ruleId?: RuleId;

  /**
   * Multiple rule IDs associated with this filter component.
   * When set, ctrl+clicking navigates to the first match in the filter output,
   * and ctrl+clicking any of these rules in the output navigates back here.
   */
  ruleIds?: RuleId[];

  prefixMatch?: boolean;

  /** Returns the effective list of rule IDs (merging ruleId and ruleIds). */
  get effectiveRuleIds(): RuleId[] {
    if (this.ruleIds && this.ruleIds.length > 0) {
      return this.ruleIds;
    }
    return this.ruleId ? [this.ruleId] : [];
  }

  /** Returns whether this component has any rule ID configured. */
  get hasRuleId(): boolean {
    return this.effectiveRuleIds.length > 0;
  }

  @HostBinding('attr.data-filter-rule-id')
  get dataFilterRuleId(): string | null {
    const ids = this.effectiveRuleIds;
    return ids.length > 0 ? ids[0] : null;
  }

  @HostBinding('attr.data-filter-rule-ids')
  get dataFilterRuleIds(): string | null {
    const ids = this.effectiveRuleIds;
    return ids.length > 1 ? ids.join(',') : null;
  }

  constructor() {
    // Inject services in constructor to ensure proper DI context
    this.ctrlKeyService = inject(CtrlKeyService);
    this.navigationService = inject(NavigationService);
    this.settings = inject(SettingsService);
    this.elementRef = inject(ElementRef);

    // Watch for Ctrl key state changes and update hover class accordingly
    effect(() => {
      if (!this.settings.enableRuleNavigation()) return;

      const isCtrlHeld = this.ctrlKeyService.isCtrlHeld();
      if (this.isHovered && this.hasRuleId) {
        const titleTextElement = this.elementRef.nativeElement.querySelector('.filter-title-text') as HTMLElement;
        if (titleTextElement) {
          if (isCtrlHeld) {
            titleTextElement.classList.add('hovered');
          } else {
            titleTextElement.classList.remove('hovered');
          }
        }
      }
    });
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    if (!this.settings.enableRuleNavigation()) return;

    this.isHovered = true;
    if (this.ctrlKeyService.isCtrlHeld() && this.hasRuleId) {
      const titleTextElement = this.elementRef.nativeElement.querySelector('.filter-title-text') as HTMLElement;
      if (titleTextElement) {
        titleTextElement.classList.add('hovered');
      }
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.settings.enableRuleNavigation()) return;

    this.isHovered = false;
    const titleTextElement = this.elementRef.nativeElement.querySelector('.filter-title-text') as HTMLElement;
    if (titleTextElement) {
      titleTextElement.classList.remove('hovered');
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;

    const ids = this.effectiveRuleIds;
    if (ids.length > 0 && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.stopPropagation();

      this.navigationService.navigateToFilterOutput(ids, this.prefixMatch);
    }
  }
}
